import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { TextField, Stack } from '@mui/material';

const CommitteeDetail: React.FC = () => {
  const { chamber, committeeCode } = useParams<{ chamber: string; committeeCode: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<any | null>(null);
  const [bills, setBills] = useState<any[] | null>(null);
  const [meetings, setMeetings] = useState<any[] | null>(null);
  const [votes, setVotes] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  // Notes (committee)
  const [noteText, setNoteText] = useState<string>('');
  const [noteSaved, setNoteSaved] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!chamber || !committeeCode) return;
      try {
        setLoading(true);
        const [dResp, bResp, mResp, vResp] = await Promise.all([
          apiService.getCommitteeDetail(chamber, committeeCode),
          apiService.getCommitteeBills(chamber, committeeCode, { limit: 25, offset: 0 }),
          apiService.getCommitteeMeetings(chamber, committeeCode, { limit: 25, offset: 0 }),
          apiService.getCommitteeVotes(chamber, committeeCode, { limit: 25, offset: 0 }),
        ]);
        setDetail(dResp.data || {});
        const billsContainer = bResp.data?.bills || bResp.data?.committees?.bills || [];
        setBills(Array.isArray(billsContainer) ? billsContainer : []);
        const meetingsContainer = mResp.data?.meetings || [];
        setMeetings(Array.isArray(meetingsContainer) ? meetingsContainer : []);
        const votesContainer = vResp.data?.votes || [];
        setVotes(Array.isArray(votesContainer) ? votesContainer : []);
      } catch (e) {
        console.error('Failed to load committee detail', e);
        setDetail({});
        setBills([]);
        setMeetings([]);
        setVotes([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [chamber, committeeCode]);

  const normalizeMember = (m: any) => {
    const src = m?.member || m;
    if (!src) return null;
    const name = src.name || [src.firstName, src.lastName].filter(Boolean).join(' ');
    const bioguideId = src.bioguideId || src.bioguideID || src.bioguide_id;
    return { name, bioguideId };
  };

  const handleViewBill = (b: any) => {
    const bill = b?.bill || b;
    if (!bill) return;
    const congress = bill.congress;
    const type = (bill.type || bill.bill_type || '').toLowerCase();
    const number = bill.number || bill.bill_number;
    if (congress && type && number) {
      navigate(`/bills/${congress}/${type}/${number}`);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading committee details..." />;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/committees')} sx={{ mb: 2 }}>
        Back to Committees
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {(detail?.committee?.name || detail?.name || committeeCode)}
        </Typography>
        <Box display="flex" gap={1} mb={1}>
          <Chip label={(detail?.committee?.chamber || detail?.chamber || chamber || '').toUpperCase()} />
          {detail?.committee?.code || detail?.code ? (
            <Chip label={detail?.committee?.code || detail?.code} variant="outlined" />
          ) : null}
        </Box>
        {detail?.committee?.url || detail?.url ? (
          <Typography variant="body2">
            Website: <a href={detail?.committee?.url || detail?.url} target="_blank" rel="noreferrer">{detail?.committee?.url || detail?.url}</a>
          </Typography>
        ) : null}
      </Paper>

      {/* Notes */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>My Notes</Typography>
        {noteSaved && <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>{noteSaved}</Typography>}
        {noteError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{noteError}</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="Add a note to this committee"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={async () => {
              setNoteError(null); setNoteSaved(null);
              try {
                await apiService.createNoteForTarget({ target_type: 'committee', target_ref: `${chamber}/${committeeCode}`, text: noteText.trim() });
                setNoteSaved('Saved');
                setNoteText('');
              } catch (e: any) {
                setNoteError(e?.response?.data?.detail || 'Failed to save note');
              }
            }}
            disabled={!noteText.trim()}
          >
            Save Note
          </Button>
        </Stack>
      </Paper>

      {/* Members */}
      {Array.isArray(detail?.committee?.members) && detail.committee.members.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Members</Typography>
            <List>
              {detail.committee.members.map((m: any, idx: number) => {
                const nm = normalizeMember(m);
                if (!nm) return null;
                return (
                  <ListItem key={idx} divider secondaryAction={
                    nm.bioguideId ? (
                      <Button size="small" onClick={() => navigate(`/members/${nm.bioguideId}`)}>View</Button>
                    ) : undefined
                  }>
                    <ListItemText primary={nm.name} />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Meetings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Committee Meetings</Typography>
          {Array.isArray(meetings) && meetings.length > 0 ? (
            <List>
              {meetings.map((mtg: any, idx: number) => (
                <ListItem key={idx} divider secondaryAction={
                  mtg?.url ? (
                    <Button size="small" component="a" href={mtg.url} target="_blank" rel="noreferrer">Details</Button>
                  ) : undefined
                }>
                  <ListItemText
                    primary={mtg?.title || 'Meeting'}
                    secondary={[mtg?.meetingDate, mtg?.location].filter(Boolean).join(' • ')}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">No meetings found for this committee.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Votes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Votes</Typography>
          {Array.isArray(votes) && votes.length > 0 ? (
            <List>
              {votes.map((v: any, idx: number) => {
                const billType = (v?.legislation?.type || v?.legislation_type || '').toLowerCase();
                const billNumber = v?.legislation?.number || v?.legislation_number;
                const congressNum = v?.congress;
                const canViewBill = billType && billNumber && congressNum;
                return (
                  <ListItem key={idx} divider secondaryAction={
                    <Box display="flex" gap={1}>
                      {v?.url && (
                        <Button size="small" component="a" href={v.url} target="_blank" rel="noreferrer">View Vote</Button>
                      )}
                      {canViewBill && (
                        <Button size="small" variant="outlined" onClick={() => navigate(`/bills/${congressNum}/${billType}/${billNumber}`)}>Bill</Button>
                      )}
                    </Box>
                  }>
                    <ListItemText
                      primary={(v?.question || '').trim() || 'Vote'}
                      secondary={[v?.voteType, v?.result].filter(Boolean).join(' — ')}
                    />
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">No votes found for this committee.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Bills */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Associated Bills</Typography>
          {bills && bills.length > 0 ? (
            <List>
              {bills.map((b: any, idx: number) => (
                <ListItem key={idx} divider secondaryAction={
                  <Box display="flex" gap={1}>
                    <Button size="small" onClick={() => handleViewBill(b)}>View</Button>
                    {(() => {
                      const bill = b?.bill || b;
                      const id = bill?.congress && (bill?.type || bill?.bill_type) && (bill?.number || bill?.bill_number)
                        ? `${bill.congress}:${(bill.type || bill.bill_type).toLowerCase()}:${bill.number || bill.bill_number}`
                        : undefined;
                      return id ? (
                        <Button size="small" variant="outlined" onClick={() => navigate('/chat', { state: { billContext: [id] } })}>Chat</Button>
                      ) : null;
                    })()}
                  </Box>
                }>
                  <ListItemText
                    primary={(b?.bill?.title || b?.title || `${(b?.bill?.type || b?.type || '').toUpperCase()} ${b?.bill?.number || b?.number || ''}`)}
                    secondary={(b?.bill?.latestAction?.text || b?.latestAction?.text || '')}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">No bills found for this committee.</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CommitteeDetail;
